import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { DEPARTMENTS, deptLabel } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Building2, Search } from "lucide-react";

export default function DepartmentsPage() {
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const { lang } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get("/stats")
      .then((r) => alive && setStats(r.data))
      .catch(() => alive && setStats({ department_wise: {} }));
    return () => { alive = false; };
  }, []);

  const items = useMemo(() => {
    const counts = stats?.department_wise || {};
    return DEPARTMENTS.map((d) => ({ name: d, count: counts[d] || 0 })).filter(
      (d) => d.name.toLowerCase().includes(q.toLowerCase())
    );
  }, [stats, q]);

  return (
    <div className="space-y-6" data-testid="departments-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-2">
          Departments · {DEPARTMENTS.length}
        </div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
          Department-wise Complaints
        </h1>
        <p className="text-slate-500 text-sm mt-2">
          Click any department to view all its complaints.
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="dept-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search department..."
          className={`pl-9 h-10 ${lang === "hi" ? "font-devanagari" : ""}`}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((d) => (
          <button
            key={d.name}
            data-testid={`dept-card-${d.name}`}
            onClick={() =>
              navigate(`/complaints?department=${encodeURIComponent(d.name)}`)
            }
            className="group text-left bg-white border border-slate-200 rounded-lg p-5 hover:border-orange-400 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-slate-100 group-hover:bg-orange-100 text-slate-700 group-hover:text-orange-700 flex items-center justify-center transition-colors">
                <Building2 className="w-5 h-5" />
              </div>
              <div className="font-mono-cms text-2xl font-bold text-slate-900 tabular-nums">
                {d.count}
              </div>
            </div>
            <div className="mt-4 font-devanagari font-semibold text-slate-900 leading-snug">
              {d.name}
            </div>
            <div className="mt-2 text-xs text-slate-500">
              {d.count === 0
                ? "No complaints yet"
                : `${d.count} complaint${d.count > 1 ? "s" : ""}`}
            </div>
          </button>
        ))}
        {items.length === 0 && (
          <div className="col-span-full py-16 text-center text-slate-500 text-sm">
            No departments match your search.
          </div>
        )}
      </div>
    </div>
  );
}
